import { useState, memo, useMemo, useEffect } from 'react';
import {
    Paper, styled, Table,
    TableBody, TableCell, tableCellClasses,
    TableHead, TableRow, Box, TextField
} from '@mui/material';
import { useTheme } from "@mui/material/styles";
import { useHighlightMarkIdx } from "./AlternateLocationGraphView";
import EditIcon from '@mui/icons-material/Edit';
import { formatValue } from '../../../../utils/currencyUtils';
import { useNavigate } from "react-router-dom";

const StyledTableCell = styled(TableCell)(({ theme, bgcolor, color, sticky, header, dense }) => ({
    width: 95,
    letterSpacing: 'normal',
    padding: dense ? '5px 16px' : '9px 16px',
    fontSize: 13,
    borderBottom: 'none',
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: bgcolor ? bgcolor : theme.palette.common.black,
        color: color ? color : theme.palette.common.white
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 13,
        paddingLeft: 8,
        paddingRight: 5
    },
    ...(sticky && {
        position: "sticky",
        left: 0,
        zIndex: header ? 5 : 3,
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white
    })
}));

const StyledTableRow = styled(TableRow)(({ bgcolor, color, fontWeight }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: bgcolor ? bgcolor : undefined,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
    backgroundColor: bgcolor,
    color,
    fontWeight
}));
const EDITABLE_LABELS = ["Total Supply", "Total Demand"];

const convertWhereUsedResponse = (rowsArray) => {
    if (!Array.isArray(rowsArray)) return [];

    const result = [];
    const measureOrder = {
        "Total Supply": 0,
        "Total Demand": 1,
        "Projected Inventory": 2
    };

    rowsArray.forEach(([itemNo, measures]) => {
        result.push({ type: "itemHeader", value: itemNo });     // Item header row
            
        Object.values(measures).forEach(measure => {            // Measure rows
            const idx = measureOrder[measure.name];

            if (idx !== undefined) {
                result.push({
                    type: "measureRow",
                    groupIndex: idx,
                    measureName: measure.name,
                    pastDue: "0.00",
                    quantities: measure.quantities.map(q => ({
                        value: q,
                        exception: null
                    }))
                });
            }
        });
    });
    return result;
};

export const AlternateLocationRenderTable = memo(({ columns, rows, editedRows, currentView = "", handleValueChange = () => { }, inventoryId, label }) => {
    const theme = useTheme();
    const { highlightMarkIdx } = useHighlightMarkIdx();

    const groupedRows = useMemo(() => {
        if (!rows) return [];

        if (label === "whereUsed") {
            return convertWhereUsedResponse(rows);
        }
        const result = [];

        Object.entries(editedRows).forEach(([siteKey, siteData]) => {
            const plantName = siteData.plantDetails?.plantName || siteKey;
            const materialNumber = siteData.plantDetails?.materialNumber || siteKey;
            const onHandQty = siteData.onHand?.totalQuantity ?? 0;

            result.push({ type: 'siteHeader', plantName, materialNumber, onHandQty });          // Site header row

            // Measure rows
            siteData.measures.forEach((measure, idxInGroup) => {
                result.push({
                    type: 'measureRow',
                    groupIndex: idxInGroup,                                                     // track index within group
                    siteKey,
                    measureIndex: idxInGroup,
                    measureName: measure.name,
                    pastDue: measure.pastDue || null,
                    quantities: measure.quantities.map(q => ({
                        value: q.quantity,
                        exception: q.exceptionType
                    }))
                });
            });
        });
        return result;
    }, [rows, editedRows, label]);

    return (
        <Paper sx={{ width: '100%' }}>
            <Table stickyHeader aria-label="customized table"
                sx={{
                    borderCollapse: "separate",
                    tableLayout: "fixed",
                    minWidth: (columns.length * 136)
                }}>
                <TableHead>
                    <TableRow>
                        <StyledTableCell sticky header
                            bgcolor={theme.palette.common.black}>Measures</StyledTableCell>
                        {columns.map((d, i) => (
                            <StyledTableCell
                                key={`date-${i}`}
                                bgcolor={highlightMarkIdx === i ? "#fff" : ''}
                                color={highlightMarkIdx === i ? "#000" : ''}
                                sx={{
                                    paddingLeft: 1,
                                    paddingRight: 2,
                                    textAlign: 'center',
                                    backgroundImage: 'var(--Paper-overlay)',
                                    boxShadow: highlightMarkIdx === i
                                        ? '0px 2px 1px -1px rgba(77, 15, 15, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12'
                                        : '',
                                    transition: 'background 0.3s ease'
                                }}
                            >
                                <div>{d.split('\n')[0]}</div>
                                <div>{d.split('\n')[1]}</div>
                            </StyledTableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {groupedRows.map((row, idx) => (
                        <RowCell
                            idx={idx}
                            row={row}
                            columns={columns}
                            onValueChange={handleValueChange}
                            currentView={currentView}
                            label={label}
                        />
                    ))}
                </TableBody>
            </Table>
        </Paper>
    );
});

const RowCell = memo(({ idx, row, columns, onValueChange, currentView, label }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isEditableRow = currentView.simulate && EDITABLE_LABELS.includes(row.measureName);

    // Black header for site rows, normal color for measure rows
    if (row.type === 'siteHeader' || row.type === 'itemHeader') {
        return (
            <StyledTableRow bgcolor='#6b6a6aff' color={theme.palette.common.white}
                sx={{
                    '& td': {
                        color: theme.palette.common.white,      // force all table cell text white
                        backgroundColor: '#6b6a6aff',        // prevent MUI hover blue overlay
                    }
                }}>
                <StyledTableCell sticky
                    bgcolor="#6b6a6aff"
                    color={theme.palette.common.white}
                    sx={{
                        fontWeight: 'bold',
                        cursor: row.type === 'siteHeader' ? 'pointer' : 'default',
                        textDecoration: row.type === 'siteHeader' ? 'underline' : 'none',
                    }}
                    onClick={row.type === 'siteHeader' ? () =>
                        navigate(`/core/inventory/node/inventory/inventory-detail/${row.materialNumber}/${row.plantName.split("::")[0]}`)
                        : undefined}
                >
                    {row.type === 'siteHeader' ? `${row.plantName}::${row.materialNumber}` : row.value}
                </StyledTableCell>
                {columns.map((_, colIdx) => (
                    <StyledTableCell
                        key={colIdx}
                        bgcolor="#6b6a6aff"
                        color={theme.palette.common.white}
                        sx={{ textAlign: 'center', fontWeight: 'bold', whiteSpace: 'nowrap' }}
                    >
                        {colIdx === 0 && row.type === 'siteHeader' ? `(On Hand: ${formatValue(row.onHandQty)})` : ''}
                    </StyledTableCell>
                ))}
            </StyledTableRow>
        );
    }
    if (row.type === 'measureRow') {
        const isOddInGroup = row.groupIndex % 2 === 1;
        const bgColor = isOddInGroup ? theme.palette.action.hover : theme.palette.common.white;

        return (
            <StyledTableRow key={`row-${idx}`} bgcolor={bgColor} color={theme.palette.common.black}>
                <StyledTableCell sticky bgcolor={bgColor} color={theme.palette.common.black} dense
                    component="th"
                    scope="row"
                    sx={{ fontWeight: 'inherit', color: '' }}>
                    {row.measureName}
                    {row.pastDue && (
                        <>
                            <br />
                            <span style={{ fontSize: "11px" }}>Past Due: {formatValue(row.pastDue)}</span>
                        </>
                    )}
                </StyledTableCell>
                {columns.map((_, colIdx) => (
                    <EditRowCell
                        idx={colIdx}
                        siteKey={row.siteKey}
                        measureIndex={row.measureIndex}
                        isEditable={isEditableRow}
                        d={row.quantities[colIdx]}
                        onValueChange={onValueChange}
                        bgcolor={bgColor}
                        color={theme.palette.common.black}
                        dense
                    />
                ))}
            </StyledTableRow>
        );
    }
    return null;
});

const EditRowCell = memo(({ idx, siteKey, measureIndex, isEditable, d, onValueChange, bgcolor, color, dense }) => {
    const [editCell, setEditCell] = useState(null);
    const { highlightMarkIdx } = useHighlightMarkIdx();
    const enabled = editCell === `${siteKey}-${measureIndex}-${idx}`;
    const [cellValue, setCellValue] = useState(d?.value);

    useEffect(() => {
        setCellValue(d?.value);
    }, [d?.value]);

    const onChange = (value) => {
        setCellValue(value);
        onValueChange(siteKey, measureIndex, idx, value);
    };

    return (
        <StyledTableCell
            bgcolor={bgcolor}
            color={color}
            dense={dense}
            sx={{
                textAlign: 'center',
                backgroundColor: highlightMarkIdx === idx ? '#f2f3f4' : bgcolor,
                color: color,
                backgroundImage: 'var(--Paper-overlay)',
                boxShadow: highlightMarkIdx === idx
                    ? '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12'
                    : '',
                transition: 'background 0.3s ease'
            }}
        >
            {isEditable ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <TextField
                        variant="standard"
                        value={cellValue}
                        onChange={(e) => onChange(e.target.value)}
                        disabled={!enabled}
                        inputProps={{ style: { fontSize: 14, width: 70 } }}
                    />
                    <EditIcon
                        fontSize="small"
                        sx={{
                            ml: 0.5,
                            color: enabled ? "#000" : "#9aa0a6",
                            cursor: "pointer"
                        }}
                        onClick={() => setEditCell(`${siteKey}-${measureIndex}-${idx}`)}
                    />
                </Box>
            ) : (
                formatValue(cellValue)
            )}
        </StyledTableCell>
    );
});